import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { Pagination } from '~/components/Pagination/Pagination';
import { Table } from '~/components/Table';
import { showToast } from '~/components/Toast';
import { usePagination } from '~/hooks/usePagination';
import { useUser } from '~/modules/auth/hooks/useUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useDefaultSort } from '~/modules/settings/hooks/useDefaultSort';
import type { LabelGroupListItem } from '~settings/api/lib/schemas';
import { useLabelGroupDelete } from '~settings/api/mutations/useLabelGroupDelete';
import { useLabelGroupList } from '~settings/api/queries/labelGroupList';
import { useModalContext } from '~settings/components/Modal';
import { Toolbar } from '~settings/components/Toolbar';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { useSearch } from '~settings/hooks/useSearch';
import { getDeleteMessageFromStatus } from '~settings/modules/label-group/helpers';

import { useLabelGroupsColumns } from './hooks/useLabelGroupsColumns';

const labelGroupEmptyArray: LabelGroupListItem[] = [];

export function ListLabelGroupPage() {
  const { hasPermission } = useUser();
  const { confirm } = useModalContext();
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  const [isPaginationFeatureEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsPssf,
  ]);
  const canCreateLabelGroup = hasPermission('LABEL_GROUP.CREATE');

  const { mutate: deleteLabelGroup } = useLabelGroupDelete({
    onError: (error) => {
      const status = error?.response?.status;
      const message = getDeleteMessageFromStatus(status);

      showToast({
        title: t(message.title),
        description: t(message.description),
        type: 'error',
      });
    },
    onSuccess: (_, variables) => {
      showToast({
        title: t('labelGroup.deleteSuccessMessage', {
          name: variables.name,
          interpolation: { escapeValue: false },
        }),
        type: 'success',
      });
    },
  });

  const { searchTerm } = useSearch();
  const { sorting, setSorting, orderBy } = useDefaultSort({
    syncWithQueryParams: true,
  });

  // Get first orderBy and split it into id and desc
  // TODO: refactor this out when the API supports multiple orderBy
  const [firstOrderBy, firstDir] = orderBy?.split(',')?.[0]?.split(':') || [];

  const { page, setPageNumber, perPage, skip, take } = usePagination();

  const { isLoading, isFetching, isError, labelGroupList } = useLabelGroupList({
    skip,
    take,
    ...(firstOrderBy && { sortBy: firstOrderBy }),
    ...(firstDir && { orderDir: firstDir }),
    ...(searchTerm && { search: searchTerm }),
  });

  const onClick = (row: LabelGroupListItem) => {
    navigate(SETTINGS_ROUTES.LABEL_GROUP_DETAIL(row.id));
  };

  const onDelete = (id: string, name: string) => {
    confirm({
      title: (
        <span>
          <Icon
            variant="regular"
            type="triangle-exclamation"
            className="mr-sm"
          />
          {t('labelGroup.deleteDialogTitle')}
        </span>
      ),
      message: t('labelGroup.deleteDialogMessage', {
        name,
        interpolation: { escapeValue: false },
      }),
      confirmText: t('buttons.confirmDelete'),
      cancelText: t('buttons.cancel'),
      onConfirm: () => {
        deleteLabelGroup({ id, name });
      },
    });
  };

  const columns = useLabelGroupsColumns(onDelete);

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <section className="py-md">Something went wrong.</section>;
  }

  return (
    <section>
      {isPaginationFeatureEnabled ? (
        <>
          <Toolbar
            placeholder={t('labelGroup.search')}
            setPageNumber={setPageNumber}
            rightAddon={
              canCreateLabelGroup ? (
                <Button
                  asChild
                  variant="primary"
                  className="shrink-0 self-center"
                  size="large"
                >
                  <Link to={SETTINGS_ROUTES.LABEL_GROUP_CREATE}>
                    {t('labelGroup.addLink')}
                  </Link>
                </Button>
              ) : null
            }
          />
          <div className="flex">
            <Pagination
              page={page}
              total={labelGroupList!.total}
              perPage={perPage}
              entity="labelGroups"
              setPageNumber={setPageNumber}
            />
          </div>
        </>
      ) : null}
      <Table
        data={labelGroupList?.data ?? labelGroupEmptyArray}
        columns={columns}
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{
          isLoading: isFetching,
          prefillRowsNumber: perPage,
        }}
      />
    </section>
  );
}

ListLabelGroupPage.displayName = 'ListLabelGroupPage';
